import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area
} from 'recharts';
import {
  Download,
  Refresh,
  TrendingUp,
  ShoppingCart,
  Inventory2,
  Payment,
  Category,
  Schedule,
  Assessment,
  Info
} from '@mui/icons-material';
import orderService from '../services/orderService';
import webOrderService from '../services/webOrderService';
import './SalesAnalytics.css';

const SalesAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('today');
  const [reportData, setReportData] = useState({
    cashierOrders: [],
    webOrders: [],
    combinedStats: {},
    productSales: [],
    categorySales: [],
    paymentMethods: [],
    hourlySales: []
  });
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    loadReportData();
  }, [timeRange]);

  const loadReportData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [cashierOrdersResponse, webOrdersResponse] = await Promise.all([
        orderService.getAllOrders({ per_page: 100 }),
        webOrderService.getAllWebOrders({ per_page: 100 })
      ]);

      const cashierOrders = cashierOrdersResponse.data?.data || [];
      const webOrders = webOrdersResponse.data?.data || [];

      const processedData = processReportData(cashierOrders, webOrders);
      
      setReportData(processedData);
      setLastUpdated(new Date());
      setLoading(false);

    } catch (error) {
      console.error('Error loading report data:', error);
      setError('Failed to load report data: ' + (error.message || 'Unknown error'));
      setLoading(false);
    }
  };

  const processReportData = (cashierOrders, webOrders) => {
    const allOrders = [...cashierOrders, ...webOrders.map(order => ({
      ...order,
      order_type: 'web'
    }))];

    // Product sales analysis
    const productSalesMap = {};
    allOrders.forEach(order => {
      order.items?.forEach(item => {
        const productName = item.product_name;
        if (!productSalesMap[productName]) {
          productSalesMap[productName] = {
            name: productName,
            sales: 0,
            quantity: 0,
            revenue: 0
          };
        }
        productSalesMap[productName].sales += 1;
        productSalesMap[productName].quantity += item.quantity;
        productSalesMap[productName].revenue += parseFloat(item.total_price);
      });
    });

    const productSales = Object.values(productSalesMap)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10);

    // Category analysis
    const categorySalesMap = {};
    allOrders.forEach(order => {
      order.items?.forEach(item => {
        const category = extractCategory(item.product_name);
        if (!categorySalesMap[category]) {
          categorySalesMap[category] = {
            name: category,
            value: 0,
            revenue: 0
          };
        }
        categorySalesMap[category].value += item.quantity;
        categorySalesMap[category].revenue += parseFloat(item.total_price);
      });
    });

    const categorySales = Object.values(categorySalesMap);

    // Payment methods analysis
    const paymentMethodsMap = {};
    allOrders.forEach(order => {
      const method = order.payment_method || 'unknown';
      if (!paymentMethodsMap[method]) {
        paymentMethodsMap[method] = {
          name: method,
          value: 0,
          amount: 0
        };
      }
      paymentMethodsMap[method].value += 1;
      paymentMethodsMap[method].amount += parseFloat(order.total_amount);
    });

    const paymentMethods = Object.values(paymentMethodsMap);

    // Hourly sales analysis
    const hourlySales = Array.from({ length: 24 }, (_, i) => ({
      hour: `${i}:00`,
      sales: 0,
      revenue: 0
    }));

    allOrders.forEach(order => {
      const hour = new Date(order.created_at).getHours();
      hourlySales[hour].sales += 1;
      hourlySales[hour].revenue += parseFloat(order.total_amount);
    });

    // Combined stats
    const combinedStats = {
      totalOrders: cashierOrders.length + webOrders.length,
      totalRevenue: allOrders.reduce((sum, order) => sum + parseFloat(order.total_amount), 0),
      cashierOrders: cashierOrders.length,
      webOrders: webOrders.length,
      averageOrderValue: allOrders.length > 0 ? 
        allOrders.reduce((sum, order) => sum + parseFloat(order.total_amount), 0) / allOrders.length : 0,
      totalProductsSold: allOrders.reduce((sum, order) => 
        sum + order.items.reduce((itemSum, item) => itemSum + item.quantity, 0), 0
      )
    };

    return {
      cashierOrders,
      webOrders,
      combinedStats,
      productSales,
      categorySales,
      paymentMethods,
      hourlySales
    };
  };

  const extractCategory = (productName) => {
    const name = productName.toLowerCase();
    if (name.includes('chocolate') || name.includes('candy')) return 'Chocolates & Candies';
    if (name.includes('drink') || name.includes('soda') || name.includes('water')) return 'Beverages';
    if (name.includes('spread') || name.includes('sauce')) return 'Spreads & Sauces';
    if (name.includes('biscuit') || name.includes('cookie')) return 'Biscuits & Cookies';
    return 'Other';
  };

  const formatCurrency = (amount) => {
    return `LKR ${(amount || 0).toLocaleString('en-LK', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatNumber = (number) => {
    return (number || 0).toLocaleString('en-LK');
  };

  const handleExport = () => {
    try {
      const exportData = reportData.productSales.map(product => ({
        'Product Name': product.name,
        'Quantity Sold': product.quantity,
        'Total Revenue': product.revenue,
        'Number of Orders': product.sales
      }));

      if (exportData.length > 0) {
        const excelExport = require('../../../utils/excelExport');
        excelExport.exportToExcel(exportData, 'Sales_Analytics_Report');
      } else {
        setError('No data available for export');
      }
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export data: ' + err.message);
    }
  };

  // Chart colors
  const COLORS = ['#6366F1', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  const renderMetricCard = (title, value, subtitle = '', format = 'currency', color = 'primary') => {
    const formattedValue = format === 'currency' ? formatCurrency(value) : formatNumber(value);

    return (
      <Card className="sa-metric-card">
        <CardContent className="sa-metric-content">
          <div className="sa-metric-header">
            <Typography className="sa-metric-title">
              {title}
            </Typography>
            {subtitle && (
              <Tooltip title={subtitle}>
                <Info className="sa-metric-info" />
              </Tooltip>
            )}
          </div>
          <Typography className={`sa-metric-value sa-metric-${color}`}>
            {formattedValue}
          </Typography>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box className="sa-container">
        <Box className="sa-loading-container">
          <CircularProgress size={60} className="sa-loading-spinner" />
          <Typography className="sa-loading-text">
            Loading sales analytics...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="sa-container">
      {/* Header */}
      <Card className="sa-header-card">
        <CardContent className="sa-header-content">
          <div className="sa-header-main">
            <div className="sa-header-text">
              <Typography className="sa-header-title">Sales Analytics</Typography>
              <Typography className="sa-header-subtitle">
                Comprehensive sales insights and performance metrics across all channels
              </Typography>
            </div>
          </div>
          <div className="sa-header-actions">
            <Tooltip title="Refresh Data">
              <IconButton 
                onClick={loadReportData} 
                disabled={loading}
                className="sa-refresh-button"
              >
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button
              startIcon={<Download />}
              onClick={handleExport}
              disabled={loading}
              className="sa-export-button"
            >
              Export Report
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Controls */}
      <Card className="sa-controls-card">
        <CardContent className="sa-controls-content">
          <div className="sa-controls-header">
            <Typography className="sa-controls-title">Time Range</Typography>
            {lastUpdated && (
              <Typography className="sa-updated-text">
                Last updated: {lastUpdated.toLocaleString()}
              </Typography>
            )}
          </div>
          <div className="sa-controls-grid">
            <FormControl fullWidth className="sa-select-control">
              <InputLabel>Select Time Range</InputLabel>
              <Select
                value={timeRange}
                label="Select Time Range"
                onChange={(e) => setTimeRange(e.target.value)}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">This Week</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
                <MenuItem value="year">This Year</MenuItem>
              </Select>
            </FormControl>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert 
          severity="error" 
          className="sa-error-alert"
          onClose={() => setError('')}
          action={
            <Button color="inherit" size="small" onClick={loadReportData}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Key Metrics */}
      <div className="sa-metrics-section">
        <Typography className="sa-section-title">Performance Overview</Typography>
        <div className="sa-metrics-grid">
          {renderMetricCard(
            'Total Revenue',
            reportData.combinedStats.totalRevenue,
            'Total revenue from all sales channels',
            'currency',
            'primary'
          )}
          {renderMetricCard(
            'Total Orders',
            reportData.combinedStats.totalOrders,
            'Total number of transactions processed',
            'number',
            'secondary'
          )}
          {renderMetricCard(
            'Products Sold',
            reportData.combinedStats.totalProductsSold,
            'Total quantity of products sold',
            'number',
            'success'
          )}
          {renderMetricCard(
            'Average Order Value',
            reportData.combinedStats.averageOrderValue,
            'Average revenue per order',
            'currency',
            'warning'
          )}
        </div>
        
        {/* Channel Breakdown */}
        <div className="sa-channel-metrics">
          <Card className="sa-channel-card">
            <CardContent className="sa-channel-content">
              <div className="sa-channel-header">
                <ShoppingCart className="sa-channel-icon" />
                <Typography className="sa-channel-title">Order Channel Breakdown</Typography>
              </div>
              <div className="sa-channel-stats">
                <div className="sa-channel-stat">
                  <Typography className="sa-channel-label">Store Orders</Typography>
                  <Chip 
                    label={formatNumber(reportData.combinedStats.cashierOrders)} 
                    className="sa-channel-chip sa-store"
                  />
                </div>
                <div className="sa-channel-stat">
                  <Typography className="sa-channel-label">Web Orders</Typography>
                  <Chip 
                    label={formatNumber(reportData.combinedStats.webOrders)} 
                    className="sa-channel-chip sa-web"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="sa-charts-section">
        <Typography className="sa-section-title">Sales Analytics</Typography>
        <div className="sa-charts-grid">
          {/* Sales Comparison */}
          <Card className="sa-chart-card">
            <CardContent className="sa-chart-content">
              <div className="sa-chart-header">
                <Assessment className="sa-chart-icon" />
                <Typography className="sa-chart-title">Sales by Order Type</Typography>
              </div>
              <div className="sa-chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={[
                    { 
                      name: 'Store Orders', 
                      orders: reportData.combinedStats.cashierOrders, 
                      revenue: reportData.cashierOrders.reduce((sum, order) => sum + parseFloat(order.total_amount), 0) 
                    },
                    { 
                      name: 'Web Orders', 
                      orders: reportData.combinedStats.webOrders, 
                      revenue: reportData.webOrders.reduce((sum, order) => sum + parseFloat(order.total_amount), 0) 
                    }
                  ]} className="sa-bar-chart">
                    <CartesianGrid className="sa-chart-grid" />
                    <XAxis dataKey="name" className="sa-chart-axis" />
                    <YAxis className="sa-chart-axis" />
                    <RechartsTooltip 
                      formatter={(value) => [formatNumber(value), 'Value']}
                      className="sa-chart-tooltip"
                    />
                    <Legend className="sa-chart-legend" />
                    <Bar dataKey="orders" fill="#6366F1" name="Number of Orders" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="revenue" fill="#10B981" name="Revenue (LKR)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Top Selling Products */}
          <Card className="sa-chart-card">
            <CardContent className="sa-chart-content">
              <div className="sa-chart-header">
                <TrendingUp className="sa-chart-icon" />
                <Typography className="sa-chart-title">Top Selling Products</Typography>
              </div>
              <div className="sa-chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={reportData.productSales} layout="vertical" className="sa-bar-chart">
                    <CartesianGrid className="sa-chart-grid" horizontal={false} />
                    <XAxis type="number" className="sa-chart-axis" />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      width={100} 
                      className="sa-chart-axis"
                      tick={{ fontSize: 12 }}
                    />
                    <RechartsTooltip 
                      formatter={(value) => [formatCurrency(value), 'Revenue']}
                      className="sa-chart-tooltip"
                    />
                    <Bar dataKey="revenue" fill="#8B5CF6" name="Revenue" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Category Distribution */}
          <Card className="sa-chart-card">
            <CardContent className="sa-chart-content">
              <div className="sa-chart-header">
                <Category className="sa-chart-icon" />
                <Typography className="sa-chart-title">Sales by Category</Typography>
              </div>
              <div className="sa-chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart className="sa-pie-chart">
                    <Pie
                      data={reportData.categorySales}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={80}
                      dataKey="value"
                    >
                      {reportData.categorySales.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      formatter={(value) => [formatNumber(value), 'Quantity']}
                      className="sa-chart-tooltip"
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Payment Methods */}
          <Card className="sa-chart-card">
            <CardContent className="sa-chart-content">
              <div className="sa-chart-header">
                <Payment className="sa-chart-icon" />
                <Typography className="sa-chart-title">Payment Methods</Typography>
              </div>
              <div className="sa-chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart className="sa-pie-chart">
                    <Pie
                      data={reportData.paymentMethods}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={80}
                      dataKey="value"
                    >
                      {reportData.paymentMethods.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      formatter={(value) => [formatNumber(value), 'Transactions']}
                      className="sa-chart-tooltip"
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Hourly Sales Trend */}
          <Card className="sa-chart-card">
            <CardContent className="sa-chart-content">
              <div className="sa-chart-header">
                <Schedule className="sa-chart-icon" />
                <Typography className="sa-chart-title">Hourly Sales Trend</Typography>
              </div>
              <div className="sa-chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={reportData.hourlySales} className="sa-area-chart">
                    <CartesianGrid className="sa-chart-grid" />
                    <XAxis dataKey="hour" className="sa-chart-axis" />
                    <YAxis className="sa-chart-axis" />
                    <RechartsTooltip 
                      formatter={(value) => [formatNumber(value), 'Value']}
                      className="sa-chart-tooltip"
                    />
                    <Legend className="sa-chart-legend" />
                    <Area 
                      type="monotone" 
                      dataKey="sales" 
                      stroke="#EF4444" 
                      fill="#EF4444" 
                      fillOpacity={0.3} 
                      name="Orders" 
                    />
                    <Line 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="#6366F1" 
                      name="Revenue" 
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Order Status Distribution */}
          <Card className="sa-chart-card">
            <CardContent className="sa-chart-content">
              <div className="sa-chart-header">
                <Inventory2 className="sa-chart-icon" />
                <Typography className="sa-chart-title">Web Orders Status</Typography>
              </div>
              <div className="sa-chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart 
                    data={(() => {
                      const statusMap = {};
                      reportData.webOrders.forEach(order => {
                        statusMap[order.status] = (statusMap[order.status] || 0) + 1;
                      });
                      return Object.entries(statusMap).map(([status, count]) => ({ status, count }));
                    })()} 
                    className="sa-bar-chart"
                  >
                    <CartesianGrid className="sa-chart-grid" />
                    <XAxis dataKey="status" className="sa-chart-axis" />
                    <YAxis className="sa-chart-axis" />
                    <RechartsTooltip className="sa-chart-tooltip" />
                    <Bar dataKey="count" fill="#F59E0B" name="Orders" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Data Table */}
      <Card className="sa-table-card">
        <CardContent className="sa-table-content">
          <div className="sa-table-header">
            <Assessment className="sa-table-icon" />
            <Typography className="sa-table-title">Top Performing Products</Typography>
          </div>
          <div className="sa-table-container">
            <table className="sa-data-table">
              <thead>
                <tr>
                  <th className="sa-table-header">Product Name</th>
                  <th className="sa-table-header">Quantity Sold</th>
                  <th className="sa-table-header">Total Revenue</th>
                  <th className="sa-table-header">Orders</th>
                </tr>
              </thead>
              <tbody>
                {reportData.productSales.slice(0, 10).map((product, index) => (
                  <tr key={index} className="sa-table-row">
                    <td className="sa-table-cell">
                      <Typography className="sa-product-name">{product.name}</Typography>
                    </td>
                    <td className="sa-table-cell">
                      <Chip 
                        label={formatNumber(product.quantity)} 
                        className="sa-quantity-chip"
                      />
                    </td>
                    <td className="sa-table-cell">
                      <Typography className="sa-revenue-text">
                        {formatCurrency(product.revenue)}
                      </Typography>
                    </td>
                    <td className="sa-table-cell">
                      <Typography className="sa-orders-text">
                        {formatNumber(product.sales)}
                      </Typography>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SalesAnalytics;